import React, { useEffect, useState } from 'react';
import { LoaderComponent } from '../loaders/page-loader';
import apiService from '../../service/api-service';
// Importing the apiService which handles API calls with axios

interface PdfViewerProps {
  /** The API endpoint URL that returns the PDF */
  apiUrl: string;
  /** Optional query parameters to append to the URL */
  params?: Record<string, string>;
  /** Optional additional headers (e.g., auth or subscription keys) */
  headers?: Record<string, string>;
  /** Width of the iframe */
  width?: string;
  /** Height of the iframe */
  height?: string;
  /** Optional filename for downloading the PDF (e.g., scNo) */
  fileName?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  apiUrl,
  params = {},
  headers = {},
  width = '100%',
  height = '100%',
  fileName
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (fileName) {
      const originalTitle = document.title;
      const cleanFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      document.title = cleanFileName;
      return () => {
        document.title = originalTitle;
      };
    }
  }, [fileName]);

  useEffect(() => {
    const fetchPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        // Using apiService.get to fetch the PDF.
        // The responseType is set to 'blob' so axios returns the PDF as a blob.
        const response = await apiService.get<Blob>(apiUrl, params, {
          headers: {
            Accept: 'application/pdf',
            ...headers,
          },
          responseType: 'blob'
        });

        // Create an object URL from the file (or blob) for display in the iframe.
        // Wrapping blob into a File preserves the filename when user downloads the PDF from iframe.
        const fileObj = fileName
          ? new File([response.data], fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`, { type: 'application/pdf' })
          : response.data;
        const objectUrl = URL.createObjectURL(fileObj);
        setPdfUrl(objectUrl);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();
    // Dependencies: re-fetch if apiUrl, params, headers, or fileName change.
  }, [apiUrl, JSON.stringify(params), JSON.stringify(headers), fileName]);

  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = e.currentTarget;
    try {
      if (iframe.contentDocument && fileName) {
        const cleanFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
        iframe.contentDocument.title = cleanFileName;
      }
    } catch (err) {
      console.error('Error setting iframe document title:', err);
    }
  };

  if (loading) return <LoaderComponent messages={["Preview File Loading..."]} />;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;
  if (!pdfUrl) return <div>No PDF available</div>;

  return (
    <iframe
      src={pdfUrl ? `${pdfUrl}#toolbar=0` : undefined}
      title="PDF Viewer"
      width={width}
      height={height}
      style={{ border: 'none' }}
      onLoad={handleIframeLoad}
    />
  );
};

export default PdfViewer;
